// ---------------------------------------------------------------------------
// Market economics — resource trading, not the Market
// BUILDING's own baseCost/costMultipliers (see `constants/buildings.ts`).
// ---------------------------------------------------------------------------
/** Trade tokens required per trade, flat — independent of the traded amount. */
export const TRADE_TOKEN_COST_PER_TRADE = 1;
/** Smallest `amount` of the `from` resource a single trade may move — enforced by `TradeResourcesSchema` and, defensively, `planResourceTrade`. */
export const MIN_TRADE_AMOUNT = 100;
/** Length of the Market's global trade-count window. */
export const TRADE_WINDOW_HOURS = 6;
/** {@link TRADE_WINDOW_HOURS} in milliseconds, for epoch-boundary math (`currentTradeWindowStart`). */
export const TRADE_WINDOW_MS = TRADE_WINDOW_HOURS * 3600000;
/** Each Market level allows this many additional trades per {@link TRADE_WINDOW_HOURS} window. */
export const TRADES_PER_LEVEL_PER_WINDOW = 1;
/** Chance a successfully won (attacker-wins) Pirate flight grants +1 trade token. */
export const PIRATE_TRADE_TOKEN_CHANCE = 0.5;
/**
 * A won Pirate flight only grants its (possible) trade token if it looted
 * at least this many resources (metal+minerals+gas, scrap excluded — scrap
 * is never plunder) — a flat floor so a low-points player can still
 * qualify with a modest raid. See {@link PIRATE_TOKEN_LOOT_POINTS_FRACTION}
 * for the other half of the threshold, and `calculatePirateTokenLootThreshold`
 * in `packages/game-engine/src/trade.ts` for how the two combine.
 *
 * Deliberately based on `points.resource` specifically (economy size —
 * building/mining output), NOT `points.total`: this threshold asks "is this
 * raid worth it relative to what you already produce," which is an economic
 * question, independent of how many battle points a heavily-military player
 * has racked up. Contrast with {@link PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS},
 * the daily EARN cap, which deliberately uses `points.total` instead — see
 * that constant's doc comment.
 */
export const PIRATE_TOKEN_MIN_LOOT = 1000;
/**
 * Fraction of the raider's OWN resource score points (`points.resource`,
 * not `points.total` — see {@link PIRATE_TOKEN_MIN_LOOT}) their loot must
 * reach (or `PIRATE_TOKEN_MIN_LOOT`, whichever is higher) for a won Pirate
 * flight to grant a trade token. Discourages a well-established player from
 * farming tokens off trivial raids against a weak/undefended target once
 * their own economy has outgrown the flat floor.
 */
export const PIRATE_TOKEN_LOOT_POINTS_FRACTION = 1 / 20;
/**
 * Anti-farming cap on pirate-earned trade tokens: tokens are the only
 * volume gate on trading, and a Pirate flight (Vanguard vs. an undefended
 * target, no cooldown, no minimum opposition) would otherwise be an
 * unlimited token source — exactly the exploit class
 * `SCRAP_RECYCLE_DAILY_LIMIT` exists to prevent for the Recovery Center.
 * `PIRATE_TOKEN_WINDOW_HOURS` is its own global epoch-aligned window,
 * independent of {@link TRADE_WINDOW_HOURS} (the trade-SPEND window lives on
 * the planet; this earn-side cap lives on the player, in `PlayerPremium`).
 * The cap itself scales with the player's score — see
 * {@link PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS},
 * {@link PIRATE_TOKEN_MIN_DAILY_CAP}, and `calculatePirateTokenDailyCap` in
 * `packages/game-engine/src/trade.ts`.
 */
export const PIRATE_TOKEN_WINDOW_HOURS = 24;
/** {@link PIRATE_TOKEN_WINDOW_HOURS} in milliseconds. */
export const PIRATE_TOKEN_WINDOW_MS = PIRATE_TOKEN_WINDOW_HOURS * 3600000;
/**
 * Trade tokens earnable from Pirate flights per {@link PIRATE_TOKEN_WINDOW_HOURS}
 * window, per million player score **points.total** (resource + battle —
 * deliberately the combined score, not `points.resource` alone), before the
 * tier multiplier. Using `total` rather than `resource` means a
 * combat-focused ("warrior") player who has racked up battle points but
 * kept a modest economy still earns a proportionally higher cap — contrast
 * with {@link PIRATE_TOKEN_LOOT_POINTS_FRACTION}, the PER-FLIGHT loot gate,
 * which deliberately uses `points.resource` only (see its own doc comment).
 */
export const PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS = 10;
/** Score points corresponding to one "unit" of {@link PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS}. */
export const PIRATE_TOKEN_POINTS_UNIT = 1000000;
/**
 * Floor under the points-scaled daily cap: `PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS`
 * alone would put the cap at 0 for anyone under ~100,000 total points — most
 * of the playerbase this mechanic is meant to serve — making Pirate flights
 * an unreachable token source for them. `calculatePirateTokenDailyCap`
 * floors the (tier-scaled) computed cap up to at least this many tokens per
 * window, restoring roughly the pre-scaling flat entry-level rate.
 */
export const PIRATE_TOKEN_MIN_DAILY_CAP = 3;
/** Multiplier applied to the points-scaled pirate-token daily cap, by the player's effective premium tier. */
export const PIRATE_TOKEN_TIER_MULTIPLIER = {
    normal: 1,
    premium: 1.5,
    platinum: 2,
};
export const TRADE_PAIRS = [
    { pricier: "minerals", cheaper: "metal", sellRate: 1.1, buyCost: 1.2 },
    { pricier: "gas", cheaper: "metal", sellRate: 1.2, buyCost: 1.3 },
    { pricier: "gas", cheaper: "minerals", sellRate: 1.05, buyCost: 1.1 },
];
//# sourceMappingURL=trade.js.map